import { Bookmark, CalendarClock, Download, MessageSquareText, Plus } from "lucide-react";
import { MockAvatar } from "@/src/common/components/app-mock/avatar";
import { MOCK_COMMUNITY, MOCK_FEED_POSTS, MOCK_PEOPLE, VIEWER } from "@/src/common/components/app-mock/mock-community";
import { MOCK_TOKENS, MockCard, MockSelect } from "@/src/common/components/app-mock/primitives";
import { WebPostCard } from "@/src/common/components/app-mock/web-screens/post-card";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

const FEED_FILTERS: ReadonlyArray<string> = ["All", "Videos", "Files", "Polls", "Events"];

export function FeedRightSidebar() {
	const { palette } = useCommunityVariant();
	const active = MOCK_PEOPLE.filter((person) => person.id !== VIEWER.id).slice(0, 4);
	return (
		<div className="space-y-3" data-mock="feed-right-sidebar">
			<MockCard>
				<div className="flex items-center justify-between">
					<span className="text-[11px] font-semibold text-neutral-900">Active Now</span>
					<span className="text-[10px] text-neutral-500">View all</span>
				</div>
				<ul className="mt-2 space-y-2">
					{active.map((person) => (
						<li key={person.id} className="flex items-center gap-2" data-mock="active-member">
							<MockAvatar person={person} size={26} presence={person.presence === "online" ? "online" : "offline"} />
							<div className="min-w-0 flex-1">
								<p className="truncate text-[11px] font-medium text-neutral-900">{person.shortName}</p>
								<p className="text-[10px] text-neutral-500">{person.presence === "online" ? "Active Now" : "Last seen recently"}</p>
							</div>
							<MessageSquareText className="size-3.5" style={{ color: palette.accent }} aria-hidden="true" />
						</li>
					))}
				</ul>
			</MockCard>
			<MockCard>
				<span className="text-[11px] font-semibold text-neutral-900">Shortcuts</span>
				<ul className="mt-1.5 space-y-0.5 text-[11px] text-neutral-700" aria-hidden="true">
					<li className="flex items-center gap-2 rounded-lg px-1.5 py-1">
						<CalendarClock className="size-3.5" />
						Scheduled Posts / Drafts
					</li>
					<li className="flex items-center gap-2 rounded-lg px-1.5 py-1">
						<Bookmark className="size-3.5" />
						Saved Posts
					</li>
					<li className="flex items-center gap-2 rounded-lg px-1.5 py-1">
						<Download className="size-3.5" />
						Download Mobile App
					</li>
				</ul>
			</MockCard>
			<MockCard>
				<span className="text-[11px] font-semibold text-neutral-900">Sponsored Content</span>
				<div
					className="mt-2 flex h-16 flex-col items-center justify-center rounded-lg border border-dashed px-2 text-center text-[10px] text-neutral-500"
					style={{ borderColor: MOCK_TOKENS.border }}
				>
					Set up sponsored content for your community.
					<span className="mt-0.5 font-medium" style={{ color: palette.accent }}>
						Configure
					</span>
				</div>
			</MockCard>
		</div>
	);
}

interface WebFeedScreenProps {
	interactive?: boolean;
	maxPosts?: number;
	banner?: boolean;
	filters?: boolean;
}

export function WebFeedScreen({ interactive, maxPosts, banner = true, filters = true }: WebFeedScreenProps) {
	const { palette } = useCommunityVariant();
	const posts = maxPosts ? MOCK_FEED_POSTS.slice(0, maxPosts) : MOCK_FEED_POSTS;
	return (
		<div className="space-y-3" data-mock="feed">
			{banner ? (
				<div
					className="relative hidden h-24 overflow-hidden rounded-xl md:block"
					style={{
						background: `linear-gradient(to right, color-mix(in oklab, ${palette.accent} 30%, transparent), ${palette.accentSoft})`,
					}}
					aria-hidden="true"
					data-mock="feed-banner"
				>
					<div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent p-3">
						<p className="text-base font-bold text-white">{MOCK_COMMUNITY.name}</p>
					</div>
				</div>
			) : null}
			<div
				className="flex items-center gap-2 rounded-xl bg-white p-1.5"
				style={{ boxShadow: MOCK_TOKENS.surfaceShadow }}
				data-mock="composer"
			>
				<MockAvatar person={VIEWER} size={26} />
				<span className="flex-1 rounded-lg px-2 py-1.5 text-[11px] text-neutral-500" style={{ background: MOCK_TOKENS.surfaceSecondary }}>
					What's on your mind
				</span>
				<span
					className="inline-flex size-7 items-center justify-center rounded-xl text-white"
					style={{ background: palette.accent }}
					aria-hidden="true"
					title="New post"
				>
					<Plus className="size-3.5" />
				</span>
			</div>
			{filters ? (
				<div className="flex items-center justify-between gap-2" data-mock="feed-filters">
					<div className="hidden items-center gap-0.5 sm:flex" aria-hidden="true">
						{FEED_FILTERS.map((filter, index) => (
							<span
								key={filter}
								className="rounded-lg px-2 py-1 text-[11px] font-medium"
								style={index === 0 ? { background: "rgba(23,23,23,0.08)", color: "#171717" } : { color: "rgba(23,23,23,0.6)" }}
							>
								{filter}
							</span>
						))}
					</div>
					<div className="flex items-center gap-1.5">
						<MockSelect value="All Groups" />
						<MockSelect value="Latest" />
					</div>
				</div>
			) : null}
			{posts.map((post) => (
				<WebPostCard key={post.id} post={post} interactive={interactive} />
			))}
		</div>
	);
}
